import React, { useCallback, useRef, useState } from 'react';
import {
  CalendarDays, Upload, Check, X, AlertTriangle, RefreshCw, FileSpreadsheet, Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';
import { importsApi } from '../lib/api';
import { semaphoreVsBaseline } from '../lib/semaphore';

const SEMAPHORE_BG = {
  green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  yellow: 'bg-amber-50 border-amber-200 text-amber-800',
  red: 'bg-red-50 border-red-200 text-red-800',
  neutral: 'bg-slate-50 border-slate-200 text-slate-700',
};

function fmtEur(v) {
  if (v == null) return '—';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}
function fmtInt(v) {
  if (v == null) return '—';
  return Math.round(v).toLocaleString('it-IT');
}
function fmtPct(v) {
  if (v == null) return '—';
  return `${v.toFixed(1)}%`;
}

function yesterdayIso() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}
function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function DailyImportTab({ onImportComplete }) {
  const [date, setDate] = useState(yesterdayIso());
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef(null);
  const pendingFileRef = useRef(null);   // remember the file across preview→confirm

  const handleFiles = useCallback(async (files) => {
    const file = files?.[0];
    if (!file) return;
    pendingFileRef.current = file;
    setError(null); setSuccess(null);
    setLoading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('date', date);
    try {
      const res = await importsApi.previewDaily(fd);
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [date]);

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleConfirm = async () => {
    if (!preview || !pendingFileRef.current) return;
    setLoading(true); setError(null);
    const fd = new FormData();
    fd.append('file', pendingFileRef.current);
    fd.append('date', date);
    try {
      const res = await importsApi.uploadDaily(fd);
      setSuccess(res.data);
      setPreview(null);
      pendingFileRef.current = null;
      if (fileRef.current) fileRef.current.value = '';
      onImportComplete?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPreview(null); setError(null); setSuccess(null);
    pendingFileRef.current = null;
    if (fileRef.current) fileRef.current.value = '';
  };

  const totalSales = (preview?.entries || []).reduce(
    (a, e) => a + (e.kpi_map?.total_sales || 0), 0,
  );
  const overwriteCount = (preview?.entries || []).filter(e => e.has_existing).length;

  return (
    <div className="space-y-5" data-testid="daily-import-tab">
      {/* Header */}
      <div className="bg-white border border-slate-200 p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-slate-900 flex items-center justify-center flex-shrink-0">
            <CalendarDays className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-lg font-semibold">Import giornaliero</h2>
            <p className="text-sm text-slate-500 leading-snug">
              Carica un export Fresha di una singola giornata. Auto-rileviamo gli store dal file
              e popoliamo il calendario al volo.
            </p>
          </div>
        </div>

        {/* Date picker — large touch target on mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-[180px_auto] gap-3 items-end">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">Data dei dati</label>
            <input
              type="date"
              value={date}
              max={todayIso()}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-300 focus:border-slate-900 focus:outline-none font-mono text-sm"
              data-testid="daily-date-input"
            />
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Sparkles className="w-3.5 h-3.5" />
            Default: ieri. Cambialo se i dati sono di un altro giorno.
          </div>
        </div>
      </div>

      {/* Drop zone (hidden when previewing) */}
      {!preview && !success && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          className={`relative border-2 border-dashed transition-all cursor-pointer touch-manipulation ${
            dragActive ? 'border-slate-900 bg-slate-50' : 'border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50'
          }`}
          data-testid="daily-dropzone"
        >
          <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-6 text-center">
            <div className={`w-16 h-16 mb-4 flex items-center justify-center transition-colors ${
              dragActive ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400'
            }`}>
              <Upload className="w-7 h-7" />
            </div>
            <p className="font-heading text-lg font-semibold text-slate-900 mb-1">
              {dragActive ? 'Rilascia qui il file' : 'Trascina il CSV/Excel di Fresha'}
            </p>
            <p className="text-sm text-slate-500 mb-4">
              o tocca per selezionarlo dal dispositivo
            </p>
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-400">
              <FileSpreadsheet className="w-3.5 h-3.5" />
              .xlsx / .csv
            </span>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="daily-file-input"
          />
        </div>
      )}

      {/* Loading state */}
      {loading && !preview && (
        <div className="flex items-center gap-3 px-4 py-4 bg-white border border-slate-200" data-testid="daily-loading">
          <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-700">Lettura del file in corso…</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 px-4 py-3 border border-red-200 bg-red-50 text-red-800" data-testid="daily-error">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-semibold">Errore durante l'elaborazione</p>
            <p className="text-xs mt-0.5">{error}</p>
          </div>
          <button onClick={reset} className="p-1 hover:bg-red-100" data-testid="daily-error-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="bg-white border border-emerald-200" data-testid="daily-success">
          <div className="px-5 py-4 bg-emerald-50 flex items-start gap-3">
            <div className="w-8 h-8 bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <p className="font-heading font-semibold text-emerald-900">Import completato</p>
              <p className="text-sm text-emerald-800 mt-0.5">
                {success.rows_processed} {success.rows_processed === 1 ? 'negozio' : 'negozi'}
                {' '}importati per il <strong>{success.date}</strong>. Il calendario è già aggiornato.
              </p>
            </div>
            <button onClick={reset} className="px-3 py-1.5 border border-emerald-300 bg-white text-sm hover:bg-emerald-50" data-testid="daily-success-new">
              Nuovo import
            </button>
          </div>
          {success.unmatched?.length > 0 && (
            <div className="px-5 py-3 border-t border-emerald-200 text-xs text-amber-800">
              <strong>Negozi non riconosciuti:</strong> {success.unmatched.join(', ')}.
              Verifica le mappature in Mappatura Negozi.
            </div>
          )}
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="bg-white border border-slate-200" data-testid="daily-preview">
          <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Anteprima · pre-conferma</p>
              <p className="font-heading font-semibold text-slate-900">
                {preview.entry_count} {preview.entry_count === 1 ? 'negozio' : 'negozi'} per il <span className="font-mono">{preview.date}</span>
              </p>
              <p className="text-xs text-slate-500 mt-0.5 truncate">{preview.filename} · {fmtEur(totalSales)} totali</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={reset} className="rounded-none" data-testid="daily-preview-cancel">
                <X className="w-4 h-4 mr-1.5" /> Annulla
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={loading || preview.entries.length === 0}
                className="bg-black text-white hover:bg-slate-900 rounded-none"
                data-testid="daily-preview-confirm"
              >
                <Check className="w-4 h-4 mr-1.5" />
                {loading ? 'Salvataggio…' : (overwriteCount > 0 ? 'Conferma e sovrascrivi' : 'Conferma import')}
              </Button>
            </div>
          </div>

          {overwriteCount > 0 && (
            <div className="flex items-start gap-3 px-5 py-3 border-b border-amber-200 bg-amber-50" data-testid="daily-overwrite-warning">
              <RefreshCw className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                <strong>{overwriteCount}</strong> {overwriteCount === 1 ? 'negozio ha' : 'negozi hanno'} già dati per <strong>{preview.date}</strong>: confermando, i valori esistenti verranno sovrascritti.
              </p>
            </div>
          )}

          {preview.unmatched?.length > 0 && (
            <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 text-xs text-slate-600">
              <strong>Negozi non riconosciuti:</strong> {preview.unmatched.join(', ')} (saranno saltati)
            </div>
          )}

          {preview.entries.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500">
              Nessun negozio riconosciuto nel file.
            </div>
          ) : (
            <PreviewTable entries={preview.entries} />
          )}
        </div>
      )}
    </div>
  );
}

function PreviewTable({ entries }) {
  return (
    <>
      {/* Desktop: table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-5 py-2.5 font-semibold">Negozio</th>
              <th className="text-right px-3 py-2.5 font-semibold">Vendite</th>
              <th className="text-right px-3 py-2.5 font-semibold">Appuntamenti</th>
              <th className="text-right px-3 py-2.5 font-semibold">Occupazione</th>
              <th className="text-right px-3 py-2.5 font-semibold">Ticket medio</th>
              <th className="text-center px-5 py-2.5 font-semibold">Stato</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <PreviewRow key={e.store_id} entry={e} />
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile: stacked cards */}
      <div className="md:hidden divide-y divide-slate-200" data-testid="daily-preview-mobile">
        {entries.map((e) => (
          <PreviewCard key={e.store_id} entry={e} />
        ))}
      </div>
    </>
  );
}

function _semaphoreFor(actual, budget) {
  if (actual == null || budget == null || budget === 0) return 'neutral';
  return semaphoreVsBaseline(actual, budget);
}

function PreviewRow({ entry }) {
  const k = entry.kpi_map || {};
  const b = entry.budget_map || {};
  const sem = _semaphoreFor(k.total_sales, b.total_sales);
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`daily-preview-row-${entry.store_id}`}>
      <td className="px-5 py-3">
        <div className="font-semibold text-slate-900">{entry.store_name}</div>
        <div className="text-[10px] text-slate-400 font-mono">{entry.store_reference}</div>
      </td>
      <td className="text-right px-3 py-3 font-mono tabular-nums">{fmtEur(k.total_sales)}</td>
      <td className="text-right px-3 py-3 font-mono tabular-nums">{fmtInt(k.total_appointments)}</td>
      <td className="text-right px-3 py-3 font-mono tabular-nums">{fmtPct(k.occupancy_pct)}</td>
      <td className="text-right px-3 py-3 font-mono tabular-nums">{fmtEur(k.avg_appointment_value)}</td>
      <td className="text-center px-5 py-3">
        <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${SEMAPHORE_BG[sem]}`}>
          {sem === 'neutral' ? (entry.has_existing ? 'Aggiornamento' : 'Nuovo') : (sem === 'green' ? 'Sopra' : sem === 'red' ? 'Sotto' : 'In linea')}
        </span>
      </td>
    </tr>
  );
}

function PreviewCard({ entry }) {
  const k = entry.kpi_map || {};
  const b = entry.budget_map || {};
  const sem = _semaphoreFor(k.total_sales, b.total_sales);
  return (
    <div className="px-4 py-3" data-testid={`daily-preview-card-${entry.store_id}`}>
      <div className="flex items-baseline justify-between mb-2">
        <p className="font-semibold text-slate-900">{entry.store_name}</p>
        <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${SEMAPHORE_BG[sem]}`}>
          {sem === 'neutral' ? (entry.has_existing ? 'Update' : 'Nuovo') : (sem === 'green' ? 'Sopra' : sem === 'red' ? 'Sotto' : 'In linea')}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-y-1 gap-x-3 text-xs tabular-nums">
        <span className="text-slate-500">Vendite</span>
        <span className="font-mono font-semibold text-right">{fmtEur(k.total_sales)}</span>
        <span className="text-slate-500">Appuntamenti</span>
        <span className="font-mono text-right">{fmtInt(k.total_appointments)}</span>
        <span className="text-slate-500">Occupazione</span>
        <span className="font-mono text-right">{fmtPct(k.occupancy_pct)}</span>
        <span className="text-slate-500">Ticket medio</span>
        <span className="font-mono text-right">{fmtEur(k.avg_appointment_value)}</span>
      </div>
    </div>
  );
}
